import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { partnerBySlug } from "@/data/partners";
import { PartnerCTA, PartnerDisclosure } from "@/components/site/PartnerBlocks";
import { WhyYouNeedIt, HowItWorks } from "@/components/site/BusinessSetupBlocks";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import heroDunsNumber from "@/assets/hero/hero-duns-number.jpg.asset.json";

const HERO = {
  src: heroDunsNumber.url,
  alt: "Real estate business owner reviewing LLC documents in a bright office with a duplex rental property visible outside",
};

export const Route = createFileRoute("/business-setup/duns-number")({
  head: () =>
    buildHead({
      title: "Get a DUNS Number for Your Real Estate LLC | Simply Approved",
      description:
        "A DUNS number gives your investment LLC a business credit identity. Here is what it is, when to get one, and how investors use it.",
      url: "/business-setup/duns-number",
      type: "article",
      keywords:
        "DUNS number for real estate LLC, register DUNS number for investment property, business credit file for LLC, DUNS number for rental property entity, business credit identity investor, DUNS number vendor accounts, DUNS number requirements LLC, business credit reporting real estate, DUNS number for new LLC, entity credit history investors, business credit bureau DUNS",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Business Setup", url: "/business-setup" },
        { name: "DUNS number", url: "/business-setup/duns-number" },
      ],
      service: { name: "DUNS Number Registration for Investors" },
      image: HERO.src,
      imageAlt: HERO.alt,
    }),
  component: DunsPage,
});

function DunsPage() {
  const partner = partnerBySlug("dandb-duns")!;
  return (
    <SiteShell>
      <PageHero
        eyebrow="Business Setup"
        title="DUNS number for your investment entity"
        subtitle="Most business credit reporting is tied to a DUNS number. Registering early lets your LLC build a payment history of its own."
        image={HERO}
      />
      <PageEssentials />
      <WhyYouNeedIt
        service={"a DUNS number"}
        intro={"A DUNS number is the nine-digit identifier that most business credit bureaus and vendors use to file your entity's payment history. Without one, your LLC's on-time payments generally go nowhere \u2014 the credit you are building keeps landing on your personal profile instead."}
        reasons={[
          { title: "It is where business credit history lives", body: "Vendors, suppliers, and many lenders report to a business file keyed to the DUNS number. No file, no history." },
          { title: "Separates the entity from your personal profile", body: "The point of investing through an LLC is separation. A business credit file is the credit side of that separation." },
          { title: "Vendors and suppliers ask for it", body: "Rehab suppliers, net-30 vendors, and insurers frequently request a DUNS number when opening a business account." },
          { title: "It takes minutes and compounds over time", body: "Business credit files reward age. Registering now gives the entity a longer track record when you actually need it." },
        ]}
      />
      <Section>
        <SectionHeader eyebrow="Before you register" title="Have this ready" />
        <ul className="mt-6 grid gap-3 text-sm md:grid-cols-2">
          {[
            "Legal entity name exactly as filed with the state",
            "EIN confirmation letter",
            "Business address and phone number",
            "Formation date and entity type",
            "Primary contact for the business",
          ].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card p-4">{i}</li>
          ))}
        </ul>
        <HowItWorks
          steps={[
            "Confirm your entity name, EIN, address, and formation date match your state filing exactly.",
            "Register the DUNS number for the entity.",
            "Open vendor and supplier accounts under the entity so payment history starts reporting.",
          ]}
        />
        <PartnerCTA partner={partner} />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply" className="rounded-md bg-brand-gold px-5 py-3 text-sm font-semibold text-primary-foreground">Start My Approval</Link>
          <Link to="/business-setup/llc" className="rounded-md border border-brand px-5 py-3 text-sm font-semibold text-brand">Form your LLC first</Link>
        </div>
        <PartnerDisclosure />
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
